import React, { useState } from 'react';
import { Button, Modal } from 'antd';
import { CodeOutlined, CopyOutlined, CheckOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import formatValues, { discartNullValues } from 'utils/formatValues';

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  border: none;
  font-weight: 500;
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    overflow: hidden;
  }
  
  .ant-modal-header {
    border-bottom: none;
    padding: 20px 24px;
  }
  
  .ant-modal-title {
    font-weight: 600;
    font-size: 18px;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  
`;

const JSONContainer = styled.div`
  position: relative;
  overflow: hidden;
  background-color: #f0f2f5;
  border-radius: 12px;
`;

const JSONHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
`;

const JSONInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
`;

const CopyButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const JSONContent = styled.pre`
  margin: 0;
  padding: 24px;
  font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
  font-size: 14px;
  line-height: 1.6;
  overflow-x: auto;
  max-height: 500px;
  overflow-y: auto;
  
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  color: #888;
  text-align: center;
  
  .anticon {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }
`;

export default function ModalJSON({ json, label, disabled }) {
  const jsonString = typeof json === 'string' ? json : JSON.stringify(json, null, 6);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const onClose = () => {
    setOpen(false);
    setCopied(false);
  };

  const isPlainObject = (val) => val && typeof val === 'object' && !Array.isArray(val);

  const deepClean = (value) => {
    if (Array.isArray(value)) {
      const cleanedArray = value
        .map((item) => deepClean(item))
        .filter((item) => (
          item !== null
          && item !== undefined
          && item !== ''
          && item !== 'N/A'
          && !(Array.isArray(item) && item.length === 0)
          && !(isPlainObject(item) && Object.keys(item).length === 0)
        ));
      return cleanedArray;
    }
    if (isPlainObject(value)) {
      const topLevelClean = discartNullValues(value);
      Object.entries(topLevelClean).forEach(([key, val]) => {
        const child = deepClean(val);
        if (
          child === null
          || child === undefined
          || child === ''
          || child === 'N/A'
          || (Array.isArray(child) && child.length === 0)
          || (isPlainObject(child) && Object.keys(child).length === 0)
        ) {
          delete topLevelClean[key];
        } else {
          topLevelClean[key] = child;
        }
      });
      return topLevelClean;
    }
    return value;
  };

  const getCleanJson = () => {
    try {
      const base = typeof json === 'string' ? JSON.parse(json) : json;
      const formatted = formatValues(base);
      const cleanJson = deepClean(formatted);
      return JSON.stringify(cleanJson, null, 6).replace(/"/g, '');
    } catch (e) {
      const cleanJson = isPlainObject(json) ? deepClean(json) : {};
      return JSON.stringify(cleanJson, null, 6).replace(/"/g, '');
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error al copiar:', err);
    }
  };

  const getObjectSize = (data) => {
    try {
      return Object.keys(data).length;
    } catch {
      return 0;
    }
  };

  return (
    <>
      <StyledButton
        disabled={disabled}
        onClick={() => setOpen(true)}
        icon={<CodeOutlined />}
      >
        Ver detalle
        {' '}
        {label}
      </StyledButton>

      <StyledModal
        open={open}
        onCancel={onClose}
        footer={null}
        width={800}
        centered
        title={(
          <>
            <CodeOutlined />
            Visualizador de
            {' '}
            {label || 'JSON'}
          </>
        )}
      >
        <JSONContainer>
          <JSONHeader>
            <JSONInfo>
              <span>
                Propiedades:
                {getObjectSize(json)}
              </span>
              <span>•</span>
              <span>
                Tamaño:
                {jsonString?.length ?? 0}
                {' '}
                caracteres
              </span>
            </JSONInfo>
            {json && Object.keys(json).length > 0 && (
            <CopyButton
              onClick={handleCopy}
              icon={copied ? <CheckOutlined /> : <CopyOutlined />}
            >
              {copied ? 'Copiado' : 'Copiar'}
            </CopyButton>
            )}
          </JSONHeader>

          {json && Object.keys(json).length > 0 ? (
            <JSONContent>
              {getCleanJson()}
            </JSONContent>
          ) : (
            <EmptyState>
              <CodeOutlined />
              <div>No hay datos para mostrar</div>
            </EmptyState>
          )}
        </JSONContainer>
      </StyledModal>
    </>
  );
}

ModalJSON.propTypes = {
  json: PropTypes.oneOfType([
    PropTypes.shape({}),
    PropTypes.string,
  ]).isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
};

ModalJSON.defaultProps = {
  label: '',
  disabled: false,
};
